import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  fetchTasks,
  fetchMyTasks,
  fetchDelegatedTasks,
  fetchTasksIDelegated,
  updateTask,
  deleteTask,
  markTaskAsCompleted,
  updateTaskProgress,
  TaskStatus,
 
  fetchTasksByMapTaskId,
} from '../../store/slices/taskSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { setCurrentChannel } from '../../store/slices/chatSlice';
import { fetchChannels, fetchDirectMessages } from '../../store/slices/channelSlice';
import { fetchUsers } from '../../store/slices/userSlice';
import { API_URL, CUMIN_API_URL } from '../../config/api';
import { overlayPendingTasks } from '../../realm/outboundQueue';
import { readTasksFromRealm, realmAvailable } from '../../realm';
import CreateCuminIssueModal from '../cumin/CreateCuminIssueModal';
import ImageViewerModal from '../chat/ImageViewerModal';
import PresignedImage from '../common/PresignedImage';
import ProductivityDashboard from './ProductivityDashboard';
import TaskCreationDialog from './TaskCreationDialog';
import TaskViewDialog from './TaskViewDialog';
import TaskProgressControl, { normalizeTaskProgress } from './TaskProgressControl';
import { formatEstimateTime } from '../../utils/estimateTime';
import { taskShowsWorkflowTag, resolveTaskWorkflowChannelLabel, unfinishedSubtasksError, apiErrorMessage } from '../../utils/workflowTaskUtils';
import { compareUrgentFirst, isOpenUrgentTask } from '../../utils/urgentDeadlineUtils';
import UrgentCountdownBadge from '../chat/UrgentCountdownBadge';
import UrgentTag, { useUrgentBlink } from '../chat/UrgentTag';

const UrgentTaskCardShell = ({ task, isUrgent, children }) => {
  const estimateMinutes = Number(task?.estimateTimeInMinutes) > 0
    ? Number(task.estimateTimeInMinutes)
    : undefined;
  const blink = useUrgentBlink(isUrgent ? task?.urgentDeadlineAt : null, estimateMinutes);
  const status = task?.status;
  const statusAccent =
    status === TaskStatus.PENDING
      ? 'border-l-amber-400 bg-gradient-to-r from-amber-50 to-white dark:from-amber-950/30 dark:to-gray-800'
      : status === TaskStatus.PICK_TODAY
        ? 'border-l-violet-400 bg-gradient-to-r from-violet-50 to-white dark:from-violet-950/30 dark:to-gray-800'
        : status === TaskStatus.IN_PROGRESS
          ? 'border-l-sky-400 bg-gradient-to-r from-sky-50 to-white dark:from-sky-950/30 dark:to-gray-800'
          : status === TaskStatus.COMPLETED
            ? 'border-l-emerald-400 bg-gradient-to-r from-emerald-50 to-white dark:from-emerald-950/30 dark:to-gray-800'
            : status === TaskStatus.CANCELLED
              ? 'border-l-slate-300 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900/40 dark:to-gray-800'
              : 'border-l-primary-400 bg-white dark:bg-gray-800';
  return (
    <div
      className={`rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 border-l-4 hover:shadow-md transition-all ${
        isUrgent
          ? `bg-red-100 dark:bg-red-950/45 border-red-300 dark:border-red-700/70 border-l-red-500${blink ? ' wl-urgent-blink' : ''}`
          : statusAccent
      }`}
    >
      {children}
    </div>
  );
};

const tabToneClass = (tone, active) => {
  const map = {
    sky: active
      ? 'bg-sky-500 text-white shadow-md shadow-sky-200/70 dark:shadow-none'
      : 'bg-sky-50 text-sky-700 hover:bg-sky-100 dark:bg-sky-950/40 dark:text-sky-200 dark:hover:bg-sky-900/50',
    emerald: active
      ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200/70 dark:shadow-none'
      : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/50',
    amber: active
      ? 'bg-amber-500 text-white shadow-md shadow-amber-200/70 dark:shadow-none'
      : 'bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-900/50',
    violet: active
      ? 'bg-violet-500 text-white shadow-md shadow-violet-200/70 dark:shadow-none'
      : 'bg-violet-50 text-violet-700 hover:bg-violet-100 dark:bg-violet-950/40 dark:text-violet-200 dark:hover:bg-violet-900/50',
    rose: active
      ? 'bg-rose-500 text-white shadow-md shadow-rose-200/70 dark:shadow-none'
      : 'bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-200 dark:hover:bg-rose-900/50',
  };
  return map[tone] || map.sky;
};

const statusChipClass = (active, tone) => {
  const map = {
    all: active ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 dark:bg-gray-800 dark:text-slate-200 dark:border-gray-600',
    pending: active ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800',
    pick: active ? 'bg-violet-500 text-white' : 'bg-violet-50 text-violet-800 border-violet-200 hover:bg-violet-100 dark:bg-violet-950/40 dark:text-violet-200 dark:border-violet-800',
    progress: active ? 'bg-sky-500 text-white' : 'bg-sky-50 text-sky-800 border-sky-200 hover:bg-sky-100 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-800',
    done: active ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800',
    cancelled: active ? 'bg-slate-500 text-white' : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600',
  };
  return map[tone] || map.all;
};

const TaskManagementPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const embeddedInChat = location.pathname.startsWith('/chat/tasks');
  const { tasks, loading, error } = useSelector((state) => state.tasks);
  const { user } = useSelector((state) => state.auth);
  const { channels = [], directMessages = [] } = useSelector((state) => state.channels);
  const { users, loading: usersLoading, hasAttemptedFetch: usersFetchAttempted } = useSelector((state) => state.users);
  const messagesByChannel = useSelector((state) => state.chat.messages || {});

  const [showCreateModal, setShowCreateModal] = useState(false);
  /** When non-null, task form dialog is in edit mode. */
  const [editingTask, setEditingTask] = useState(null);
  const [renamingTaskId, setRenamingTaskId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'my', 'delegated', 'delegated-by-me'
  const [filterStatus, setFilterStatus] = useState(null);
  const [openClosedTab, setOpenClosedTab] = useState('pending');
  const [showDelegateDialog, setShowDelegateDialog] = useState(false);
  const [delegatingTask, setDelegatingTask] = useState(null);
  const [selectedDelegateIds, setSelectedDelegateIds] = useState([]);
  const [allTasksForCounts, setAllTasksForCounts] = useState([]);
  const [processing, setProcessing] = useState({});
  const [viewingTask, setViewingTask] = useState(null);
  const [mappedTasksByTaskId, setMappedTasksByTaskId] = useState({}); // Store mapped tasks for each task ID (for accordion)
  const [loadingMappedTasksByTaskId, setLoadingMappedTasksByTaskId] = useState({}); // Loading state for each task
  const [openMenuId, setOpenMenuId] = useState(null);
  const [expandedTaskIds, setExpandedTaskIds] = useState(new Set());
  const [showCuminIssueModal, setShowCuminIssueModal] = useState(false);
  const [taskForCuminIssue, setTaskForCuminIssue] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [taskMessagesByTaskId, setTaskMessagesByTaskId] = useState({}); // Store messages for each task
  const [searchQuery, setSearchQuery] = useState('');
  const [showWorkflowTasks, setShowWorkflowTasks] = useState(true);
  const [showPersonalTasks, setShowPersonalTasks] = useState(true);
  const [sortOrder, setSortOrder] = useState('newest');
  const [knownWorkflowIds, setKnownWorkflowIds] = useState(() => new Set());

  useEffect(() => {
    const handleOpenDailyTasks = (event) => {
      const tab = event?.detail?.tab;
      if (tab === 'delegated' || tab === 'my' || tab === 'delegated-by-me' || tab === 'all') {
        setActiveTab(tab);
        if (tab !== 'all') {
          setFilterStatus(null);
        }
      }
    };

    window.addEventListener('workloop:open-daily-tasks', handleOpenDailyTasks);
    return () => window.removeEventListener('workloop:open-daily-tasks', handleOpenDailyTasks);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadKnownWorkflows = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/messages/workflows-assigned`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        if (cancelled) return;
        const ids = new Set(
          (Array.isArray(response.data) ? response.data : [])
            .map((item) => item?.messageId)
            .filter(Boolean)
            .map((id) => String(id)),
        );
        setKnownWorkflowIds(ids);
      } catch {
        if (!cancelled) setKnownWorkflowIds(new Set());
      }
    };
    loadKnownWorkflows();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const loadTab = () => {
      if (activeTab === 'my') {
        dispatch(fetchMyTasks());
      } else if (activeTab === 'delegated') {
        dispatch(fetchDelegatedTasks());
      } else if (activeTab === 'delegated-by-me') {
        dispatch(fetchTasksIDelegated());
      } else if (activeTab === 'all') {
        dispatch(fetchTasks({ status: filterStatus || undefined }));
      }
    };
    loadTab();
    const onLocalTasks = () => loadTab();
    window.addEventListener('workloop:tasks-flushed', onLocalTasks);
    return () => window.removeEventListener('workloop:tasks-flushed', onLocalTasks);
  }, [dispatch, activeTab, filterStatus]);

  // Function to refresh all tasks for count calculation (always fetches unfiltered tasks)
  const refreshAllTasksForCounts = async () => {
    try {
      if (await realmAvailable()) {
        const cached = await readTasksFromRealm();
        const merged = await overlayPendingTasks(cached || []);
        setAllTasksForCounts(merged);
        return;
      }
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllTasksForCounts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch all tasks for counts:', error);
      // Fallback to current tasks ONLY if on "all" tab WITHOUT status filter (unfiltered tasks)
      if (activeTab === 'all' && !filterStatus && tasks.length > 0) {
        setAllTasksForCounts(tasks);
      }
      // If filter is active, don't use filtered tasks as fallback (keep existing allTasksForCounts)
    }
  };

  // Fetch all tasks for accurate count calculation (independent of status filters)
  useEffect(() => {
    // Always ensure allTasksForCounts has unfiltered tasks for accurate tab counts
    // Only update from current tasks if we're on "all" tab WITHOUT status filter
    if (activeTab === 'all' && !filterStatus && tasks.length > 0) {
      // When on "all" tab without status filter, current tasks are all tasks, so sync them
      setAllTasksForCounts(tasks);
    } else if (allTasksForCounts.length === 0) {
      // If we don't have allTasksForCounts yet, fetch them
      refreshAllTasksForCounts();
    }
    // If filterStatus is set, don't update allTasksForCounts (keep it with unfiltered data)
  }, [activeTab, filterStatus]); // Re-fetch when tab or filter changes

  // Update allTasksForCounts when tasks change (but ONLY if unfiltered - never use filtered tasks)
  useEffect(() => {
    // Only update allTasksForCounts from current tasks if on "all" tab WITHOUT status filter
    // This ensures tab counts are never affected by status filters
    if (activeTab === 'all' && !filterStatus && tasks.length >= 0) {
      setAllTasksForCounts(tasks);
    }
  }, [tasks, activeTab, filterStatus]); // Update when tasks change, but check filter status

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any menu
      const isClickInsideMenu = event.target.closest('.task-menu-container');
      if (!isClickInsideMenu && openMenuId !== null) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openMenuId]);

  // Fetch users once for task delegation UI.
  useEffect(() => {
    if (!usersFetchAttempted && !usersLoading) {
      dispatch(fetchUsers());
    }
  }, [dispatch, usersFetchAttempted, usersLoading]);

  // Fetch mapped tasks for expanded tasks in accordion
  useEffect(() => {
    const fetchMappedTasksForAccordion = async () => {
      const tasksToFetch = Array.from(expandedTaskIds).filter(taskId => {
        // Only fetch if we haven't already fetched or are currently loading
        return !mappedTasksByTaskId[taskId] && !loadingMappedTasksByTaskId[taskId];
      });

      for (const taskId of tasksToFetch) {
        setLoadingMappedTasksByTaskId(prev => ({ ...prev, [taskId]: true }));
        try {
          const result = await dispatch(fetchTasksByMapTaskId(taskId)).unwrap();
          setMappedTasksByTaskId(prev => ({
            ...prev,
            [taskId]: Array.isArray(result) ? result : []
          }));
        } catch (error) {
          console.error(`Failed to fetch mapped tasks for task ${taskId}:`, error);
          setMappedTasksByTaskId(prev => ({
            ...prev,
            [taskId]: []
          }));
        } finally {
          setLoadingMappedTasksByTaskId(prev => {
            const newState = { ...prev };
            delete newState[taskId];
            return newState;
          });
        }
      }
    };

    if (expandedTaskIds.size > 0) {
      fetchMappedTasksForAccordion();
    }
  }, [dispatch, expandedTaskIds]);

  // Helper function to fetch message by messageId
  const fetchMessageById = async (messageId) => {
          const token = localStorage.getItem('token');
          let message = null;

          // First, try to fetch channels if not already loaded
          let allChannels = [...channels, ...directMessages];
          if (allChannels.length === 0) {
            const [channelsResult, directMessagesResult] = await Promise.all([
              dispatch(fetchChannels()),
              dispatch(fetchDirectMessages()),
            ]);
            const fetchedChannels = channelsResult.payload || [];
            const fetchedDirectMessages = directMessagesResult.payload || [];
      allChannels = [...fetchedChannels, ...fetchedDirectMessages];
            if (allChannels.length === 0) {
              allChannels = [...channels, ...directMessages];
            }
          }

          // Search through channels' messages to find the message
          for (const channel of allChannels) {
            try {
              const response = await axios.get(
                `${API_URL}/messages/channel/${channel.id}`,
                {
                  params: { page: 0, size: 100 },
                  headers: { Authorization: `Bearer ${token}` },
                }
              );

              const messages = Array.isArray(response.data) ? response.data : (response.data.content || []);
        const foundMessage = messages.find(m => m.id === messageId);

              if (foundMessage) {
                message = foundMessage;
                break;
              }
            } catch (error) {
              console.warn(`Failed to fetch messages for channel ${channel.id}:`, error);
            }
          }

    return message;
  };

  // Fetch messages for expanded tasks in accordion view
  useEffect(() => {
    const fetchMessagesForExpandedTasks = async () => {
      const tasksToFetch = tasks.filter(task =>
        task.messageId &&
        expandedTaskIds.has(task.id) &&
        !taskMessagesByTaskId[task.id]
      );

      if (tasksToFetch.length === 0) return;

      for (const task of tasksToFetch) {
        try {
          const message = await fetchMessageById(task.messageId);
          if (message) {
            setTaskMessagesByTaskId(prev => ({
              ...prev,
              [task.id]: message
            }));
          }
        } catch (error) {
          console.error(`Failed to fetch message for task ${task.id}:`, error);
        }
      }
    };

    fetchMessagesForExpandedTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedTaskIds, tasks, dispatch]);

  const handleCreate = () => {
    setEditingTask(null);
    setShowCreateModal(true);
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setShowCreateModal(true);
  };

  const beginRenameTask = (task) => {
    setRenamingTaskId(task.id);
    setRenameDraft(task.title || '');
  };

  const cancelRenameTask = () => {
    setRenamingTaskId(null);
    setRenameDraft('');
  };

  const saveRenameTask = async (task) => {
    if (renamingTaskId !== task.id) return;
    const nextTitle = renameDraft.trim();
    if (!nextTitle || nextTitle === (task.title || '').trim()) {
      cancelRenameTask();
      return;
    }
    try {
      setProcessing((prev) => ({ ...prev, [task.id]: 'updating' }));
      await dispatch(updateTask({
        taskId: task.id,
        taskData: { title: nextTitle },
      })).unwrap();
      cancelRenameTask();
      refetchTasksAfterChange();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(error, 'Failed to rename task'),
      }));
    } finally {
      setProcessing((prev) => ({ ...prev, [task.id]: null }));
    }
  };

  const closeTaskFormDialog = () => {
    setShowCreateModal(false);
    setEditingTask(null);
  };

  const handleTaskFormSaved = () => {
      if (activeTab === 'my') {
        dispatch(fetchMyTasks());
      } else if (activeTab === 'delegated') {
        dispatch(fetchDelegatedTasks());
      } else if (activeTab === 'delegated-by-me') {
        dispatch(fetchTasksIDelegated());
      } else {
        dispatch(fetchTasks({ status: filterStatus }));
      }
      refreshAllTasksForCounts();
  };

  const handleDelete = async (taskId) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this task? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      setProcessing({ ...processing, [taskId]: 'deleting' });
      await dispatch(deleteTask(taskId)).unwrap();
      dispatch(
        addNotification({
          type: 'success',
          message: 'Task deleted successfully',
        })
      );
      // Refetch tasks based on active tab
      if (activeTab === 'my') {
        dispatch(fetchMyTasks());
      } else if (activeTab === 'delegated') {
        dispatch(fetchDelegatedTasks());
      } else if (activeTab === 'delegated-by-me') {
        dispatch(fetchTasksIDelegated());
      } else {
        dispatch(fetchTasks({ status: filterStatus }));
      }
      // Refresh counts after task operations
      refreshAllTasksForCounts();
    } catch (error) {
      console.error('Failed to delete task:', error);
      dispatch(
        addNotification({
          type: 'error',
          message: error || 'Failed to delete task',
        })
      );
    } finally {
      setProcessing({ ...processing, [taskId]: null });
    }
  };

  const assertCanCompleteTask = async (taskId) => {
    try {
      const mapped = await dispatch(fetchTasksByMapTaskId(taskId)).unwrap();
      const error = unfinishedSubtasksError(mapped);
      if (error) {
        dispatch(addNotification({ type: 'error', message: error }));
        return false;
      }
    } catch {
      // Server still validates unfinished subtasks.
    }
    return true;
  };

  const handleMarkAsCompleted = async (taskId) => {
    try {
      setProcessing({ ...processing, [taskId]: 'completing' });
      if (!(await assertCanCompleteTask(taskId))) return;
      await dispatch(markTaskAsCompleted(taskId)).unwrap();
      dispatch(
        addNotification({
          type: 'success',
          message: 'Task marked as completed',
        })
      );
      // Refetch tasks based on active tab
      if (activeTab === 'my') {
        dispatch(fetchMyTasks());
      } else if (activeTab === 'delegated') {
        dispatch(fetchDelegatedTasks());
      } else if (activeTab === 'delegated-by-me') {
        dispatch(fetchTasksIDelegated());
      } else {
        dispatch(fetchTasks({ status: filterStatus }));
      }
      // Refresh counts after task operations
      refreshAllTasksForCounts();
    } catch (error) {
      console.error('Failed to mark task as completed:', error);
      dispatch(
        addNotification({
          type: 'error',
          message: apiErrorMessage(error, 'Failed to mark task as completed'),
        })
      );
    } finally {
      setProcessing({ ...processing, [taskId]: null });
    }
  };

  const refetchTasksAfterChange = () => {
    if (activeTab === 'my') {
      dispatch(fetchMyTasks());
    } else if (activeTab === 'delegated') {
      dispatch(fetchDelegatedTasks());
    } else if (activeTab === 'delegated-by-me') {
      dispatch(fetchTasksIDelegated());
    } else {
      dispatch(fetchTasks({ status: filterStatus }));
    }
    refreshAllTasksForCounts();
  };

  const handleProgressChange = async (taskId, completionPercentage) => {
    try {
      setProcessing({ ...processing, [taskId]: 'progress' });
      if (completionPercentage >= 100 && !(await assertCanCompleteTask(taskId))) return;
      const updated = await dispatch(
        updateTaskProgress({ taskId, completionPercentage })
      ).unwrap();
      if (viewingTask?.id === taskId) {
        setViewingTask(updated);
      }
      dispatch(
        addNotification({
          type: 'success',
          message:
            completionPercentage >= 100
              ? 'Task marked as completed'
              : `Progress updated to ${completionPercentage}%`,
        })
      );
      refetchTasksAfterChange();
    } catch (error) {
      console.error('Failed to update task progress:', error);
      dispatch(
        addNotification({
          type: 'error',
          message: apiErrorMessage(error, 'Failed to update task progress'),
        })
      );
    } finally {
      setProcessing({ ...processing, [taskId]: null });
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      setProcessing({ ...processing, [taskId]: 'updating' });
      if (newStatus === TaskStatus.COMPLETED && !(await assertCanCompleteTask(taskId))) return;
      await dispatch(
        updateTask({
          taskId,
          taskData: { status: newStatus },
        })
      ).unwrap();
      dispatch(
        addNotification({
          type: 'success',
          message: 'Task status updated successfully',
        })
      );
      // Refetch tasks based on active tab
      if (activeTab === 'my') {
        dispatch(fetchMyTasks());
      } else if (activeTab === 'delegated') {
        dispatch(fetchDelegatedTasks());
      } else if (activeTab === 'delegated-by-me') {
        dispatch(fetchTasksIDelegated());
      } else {
        dispatch(fetchTasks({ status: filterStatus }));
      }
      // Refresh counts after task operations
      refreshAllTasksForCounts();
    } catch (error) {
      console.error('Failed to update task status:', error);
      dispatch(
        addNotification({
          type: 'error',
          message: apiErrorMessage(error, 'Failed to update task status'),
        })
      );
    } finally {
      setProcessing({ ...processing, [taskId]: null });
    }
  };

  const handleOpenDelegateDialog = (task) => {
    setDelegatingTask(task);
    const initialIds = Array.isArray(task.delegateId) ? task.delegateId : (task.delegateId ? [task.delegateId] : []);
    setSelectedDelegateIds(initialIds);
    setShowDelegateDialog(true);
    // Fetch users if not already loaded
    if (users.length === 0 && !usersLoading) {
      dispatch(fetchUsers());
    }
  };

  const toggleSelectedDelegate = (userId) => {
    setSelectedDelegateIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  const handleDelegateTask = async () => {
    if (!delegatingTask) return;

    try {
      setProcessing({ ...processing, [delegatingTask.id]: 'delegating' });
      await dispatch(
        updateTask({
          taskId: delegatingTask.id,
          taskData: { delegateId: selectedDelegateIds },
        })
      ).unwrap();
      dispatch(
        addNotification({
          type: 'success',
          message: selectedDelegateIds.length > 0
            ? 'Task delegated successfully'
            : 'Task delegation removed successfully',
        })
      );
      setShowDelegateDialog(false);
      setDelegatingTask(null);
      setSelectedDelegateIds([]);

      // Refetch tasks based on active tab
      if (activeTab === 'my') {
        dispatch(fetchMyTasks());
      } else if (activeTab === 'delegated') {
        dispatch(fetchDelegatedTasks());
      } else if (activeTab === 'delegated-by-me') {
        dispatch(fetchTasksIDelegated());
      } else {
        dispatch(fetchTasks({ status: filterStatus }));
      }
      // Refresh counts after task operations
      refreshAllTasksForCounts();
    } catch (error) {
      console.error('Failed to delegate task:', error);
      dispatch(
        addNotification({
          type: 'error',
          message: error || 'Failed to delegate task',
        })
      );
    } finally {
      setProcessing({ ...processing, [delegatingTask.id]: null });
    }
  };

  const handleTaskClick = async (task) => {
    if (!task.messageId) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'This task is not linked to a message.',
        })
      );
      return;
    }

    try {
      // Fetch channels if not already loaded
      if (channels.length === 0 && directMessages.length === 0) {
        await Promise.all([
          dispatch(fetchChannels()),
          dispatch(fetchDirectMessages()),
        ]);
      }

      // Get the message to find its channelId
      const token = localStorage.getItem('token');
      let message = null;
      let channelId = null;

      // Try to find the message by searching through channels
      const allChannels = [...channels, ...directMessages];

      // First, try to fetch the message directly from the API
      // We'll search through channels' messages to find it
      for (const channel of allChannels) {
        try {
          const response = await axios.get(
            `${API_URL}/messages/channel/${channel.id}`,
            {
              params: { page: 0, size: 100 },
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const foundMessage = response.data.find(m => m.id === task.messageId);
          if (foundMessage) {
            message = foundMessage;
            channelId = channel.id;
            break;
          }
        } catch (error) {
          // Continue searching other channels
          console.warn(`Failed to fetch messages for channel ${channel.id}:`, error);
        }
      }

      if (!channelId) {
        // If not found in loaded channels, try to get message info from backend
        // For now, we'll show an error and navigate to chat anyway
        dispatch(
          addNotification({
            type: 'warning',
            message: 'Could not find the message. Navigating to chat...',
          })
        );
        navigate('/chat');
        return;
      }

      // Find the channel object
      const channel = allChannels.find(c => c.id === channelId);
      if (!channel) {
        dispatch(
          addNotification({
            type: 'error',
            message: 'Channel not found.',
          })
        );
        return;
      }

      // Set the current channel
      dispatch(setCurrentChannel(channel));

      // Navigate to chat with messageId in URL for scrolling
      navigate(`/chat?messageId=${task.messageId}`);
    } catch (error) {
      console.error('Failed to navigate to message:', error);
      dispatch(
        addNotification({
          type: 'error',
          message: 'Failed to navigate to the message. Please try again.',
        })
      );
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case TaskStatus.PENDING:
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200';
      case TaskStatus.PICK_TODAY:
        return 'bg-violet-100 text-violet-800 dark:bg-violet-900/60 dark:text-violet-200';
      case TaskStatus.IN_PROGRESS:
        return 'bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200';
      case TaskStatus.COMPLETED:
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200';
      case TaskStatus.CANCELLED:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case TaskStatus.PENDING: return 'Pending';
      case TaskStatus.PICK_TODAY: return 'Pick today';
      case TaskStatus.IN_PROGRESS: return 'In Progress';
      case TaskStatus.COMPLETED: return 'Completed';
      case TaskStatus.CANCELLED: return 'Cancelled';
      default: return status || 'Pending';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Format relative time (today, yesterday, 3 days ago, etc.)
  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? '1 Week ago' : `${weeks} Weeks ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? '1 Month ago' : `${months} Months ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      return years === 1 ? '1 Year ago' : `${years} Years ago`;
    }
  };

  // Toggle accordion for a task
  const toggleTaskAccordion = (taskId) => {
    setExpandedTaskIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  // Helper function to get file extension
  const getFileExtension = (fileName) => {
    if (!fileName) return '';
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
  };

  // Helper function to format file size
  // const formatFileSize = (bytes) => {
  //   if (!bytes || bytes === 0) return '';
  //   const k = 1024;
  //   const sizes = ['B', 'KB', 'MB', 'GB'];
  //   const i = Math.floor(Math.log(bytes) / Math.log(k));
  //   return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  // };

  // Component to render task attachments (images)
  const renderTaskAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return null;

    // Filter only image attachments
    const imageAttachments = attachments.filter(att =>
      att.fileType === 'IMAGE' &&
      getFileExtension(att.fileName).toLowerCase() !== 'svg'
    );

    if (imageAttachments.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        <div className="flex flex-wrap gap-2">
          {imageAttachments.map((attachment, index) => (
            <div key={attachment.id || index} className="relative group">
              <PresignedImage
                fileKey={attachment.fileUrl}
                alt={attachment.fileName}
                className="w-24 h-24 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity border border-gray-200 dark:border-gray-700"
                loadingClassName="w-24 h-24 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-200 dark:bg-gray-700 animate-pulse"
                title="Click to view full size"
                onClick={() => setViewingImage({ url: attachment.fileUrl, fileName: attachment.fileName })}
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-opacity flex items-center justify-center pointer-events-none">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs bg-black bg-opacity-50 px-2 py-1 rounded">
                  Click to view
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Extract initial words from description for display when title is missing
  const getInitialWordsFromDescription = (description, maxWords = 8) => {
    if (!description) return 'No description';

    let text = '';

    // Check if description is HTML
    if (description.trim().startsWith('<') && description.includes('</')) {
      // Strip HTML tags
      const tmp = document.createElement('div');
      tmp.innerHTML = description;
      text = tmp.textContent || tmp.innerText || '';
    } else {
      text = description;
    }

    // Clean up whitespace and get words
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);

    if (words.length === 0) return 'No description';

    // Take first maxWords words
    const initialWords = words.slice(0, maxWords).join(' ');

    // Add ellipsis if there are more words
    return words.length > maxWords ? initialWords + '...' : initialWords;
  };

  const sameUserId = (a, b) => {
    if (a == null || b == null) return false;
    return String(a).toLowerCase() === String(b).toLowerCase();
  };

  // Resolve a single user id to a display name
  const getUserDisplayName = (userId) => {
    if (!userId) return null;
    const match = (users || []).find((u) => sameUserId(u.id, userId));
    if (match) {
      if (match.firstName || match.lastName) {
        return `${match.firstName || ''} ${match.lastName || ''}`.trim();
      }
      return match.username || match.email || null;
    }
    return null;
  };

  // Get delegate user names by IDs
  const getDelegateNameList = (delegateId) => {
    if (!delegateId) return [];
    const ids = Array.isArray(delegateId) ? delegateId : [delegateId];
    if (ids.length === 0) return [];

    return ids.map((id) => getUserDisplayName(id) || id);
  };

  // Determine if task was delegated by me or to me based on task data
  const getDelegationInfo = (task) => {
    if (!task.delegateId || !user) return null;

    const ids = Array.isArray(task.delegateId) ? task.delegateId : [task.delegateId];
    if (ids.length === 0) return null;

    const isCreatedByMe = sameUserId(task.createdByUserId, user.id);
    const isDelegatedToMe = ids.some((id) => sameUserId(id, user.id));
    const delegatedToOthers = ids.filter((id) => !sameUserId(id, user.id));
    const isDelegatedByMe = isCreatedByMe && delegatedToOthers.length > 0;

    if (!isDelegatedByMe && !(isDelegatedToMe && !isCreatedByMe)) {
      return null;
    }

    if (isDelegatedByMe) {
      return {
        label: 'Delegated to',
        names: getDelegateNameList(delegatedToOthers),
      };
    }
    if (isDelegatedToMe && !isCreatedByMe) {
      const otherCount = delegatedToOthers.length;
      const delegatorName = getUserDisplayName(task.createdByUserId);
      return {
        label: 'Delegated by',
        names: delegatorName ? [delegatorName] : getDelegateNameList(task.createdByUserId),
        suffix: otherCount > 0 ? '(to you and others)' : undefined,
      };
    }

    return null;
  };

  const DelegationInfoDisplay = ({ delegationInfo, variant = 'full', className = '' }) => {
    if (!delegationInfo?.names?.length) return null;

    const { label, names, suffix } = delegationInfo;
    const fullTooltip = `${names.join(', ')}${suffix ? ` ${suffix}` : ''}`;

    if (variant === 'compact') {
      const visibleNames = names.slice(0, 2);
      const hiddenCount = names.length - visibleNames.length;

      return (
        <span
          className={`inline-flex flex-wrap items-center gap-1 min-w-0 ${className}`}
          title={fullTooltip}
        >
          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium flex-shrink-0">
            {label}:
          </span>
          {visibleNames.map((name, index) => (
            <span
              key={`${name}-${index}`}
              className="inline-flex max-w-[7rem] truncate px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300"
            >
              {name}
            </span>
          ))}
          {hiddenCount > 0 && (
            <span className="inline-flex px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-xs text-gray-600 dark:text-gray-300 flex-shrink-0">
              +{hiddenCount}
            </span>
          )}
          {suffix && (
            <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">{suffix}</span>
          )}
        </span>
      );
    }

    return (
      <div className={`text-xs text-gray-500 dark:text-gray-400 min-w-0 ${className}`} title={fullTooltip}>
        <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
          <span className="font-medium">{label}:</span>
          {suffix && <span className="text-gray-400 dark:text-gray-500">{suffix}</span>}
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          {names.map((name, index) => (
            <span
              key={`${name}-${index}`}
              className="inline-flex max-w-full truncate px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
              title={name}
            >
              {name}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Calculate task counts for tabs using all tasks (unfiltered - independent of status filters)
  const calculateTabCounts = () => {
    // ALWAYS use allTasksForCounts for tab counts - these should never be affected by status filters
    // Tab counts represent the total number of tasks in each category, regardless of any filters
    const tasksToCount = allTasksForCounts;

    if (!tasksToCount || tasksToCount.length === 0 || !user) {
      return {
        all: 0,
        my: 0,
        delegated: 0,
        delegatedByMe: 0
      };
    }

    // Tab counts should show ALL tasks in each category (not filtered by status)
    // Exclude completed tasks and tasks delegated by me
    const allCount = tasksToCount.filter(task => {
      const ids = Array.isArray(task.delegateId) ? task.delegateId : (task.delegateId ? [task.delegateId] : []);
      const delegatedToOthers = ids.filter(id => id !== user.id);
      const isDelegatedByMe = task.createdByUserId === user.id && delegatedToOthers.length > 0;
      return task.status !== TaskStatus.COMPLETED && !isDelegatedByMe;
    }).length;
    const myCount = tasksToCount.filter((task) => {
      const ids = Array.isArray(task.delegateId) ? task.delegateId : (task.delegateId ? [task.delegateId] : []);
      const delegatedToOthers = ids.filter((id) => id !== user.id);
      return task.createdByUserId === user.id && delegatedToOthers.length === 0;
    }).length;
    const delegatedCount = tasksToCount.filter(task => {
      const ids = Array.isArray(task.delegateId) ? task.delegateId : (task.delegateId ? [task.delegateId] : []);
      return ids.includes(user.id) && task.createdByUserId !== user.id;
    }).length;
    const delegatedByMeCount = tasksToCount.filter(task => {
      const ids = Array.isArray(task.delegateId) ? task.delegateId : (task.delegateId ? [task.delegateId] : []);
      const delegatedToOthers = ids.filter(id => id !== user.id);
      return task.createdByUserId === user.id && delegatedToOthers.length > 0;
    }).length;

    return {
      all: allCount,
      my: myCount,
      delegated: delegatedCount,
      delegatedByMe: delegatedByMeCount
    };
  };

  const tabCounts = calculateTabCounts();

  const filteredTasksArray = (activeTab === 'my'
        ? tasks.filter((task) => {
          const ids = Array.isArray(task.delegateId) ? task.delegateId : (task.delegateId ? [task.delegateId] : []);
          const delegatedToOthers = ids.filter((id) => id !== user?.id);
          const isMine = task.createdByUserId === user?.id && delegatedToOthers.length === 0;
          if (!isMine) return false;
          if (openClosedTab === 'completed') return task.status === TaskStatus.COMPLETED;
          return task.status !== TaskStatus.COMPLETED && task.status !== TaskStatus.CANCELLED;
        })
    : activeTab === 'all'
      ? tasks.filter((task) => {
        const ids = Array.isArray(task.delegateId) ? task.delegateId : (task.delegateId ? [task.delegateId] : []);
        const delegatedToOthers = ids.filter(id => id !== user?.id);
        const isDelegatedByMe = task.createdByUserId === user?.id && delegatedToOthers.length > 0;
        if (openClosedTab === 'completed') {
          return task.status === TaskStatus.COMPLETED;
        }
        return task.status !== TaskStatus.COMPLETED && task.status !== TaskStatus.CANCELLED && !isDelegatedByMe;
      })
      : tasks).filter((task) => {
        // Filter by search query
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const titleMatch = task.title && task.title.toLowerCase().includes(query);
        const descriptionMatch = task.description && task.description.toLowerCase().includes(query);
        const cuminMatch = task.cuminIssueKey && task.cuminIssueKey.toLowerCase().includes(query);
        return titleMatch || descriptionMatch || cuminMatch;
      }).filter((task) => {
        const isWorkflow = taskShowsWorkflowTag(task, knownWorkflowIds);
        if (isWorkflow && !showWorkflowTasks) return false;
        if (!isWorkflow && !showPersonalTasks) return false;
        return true;
      });

  const taskCreatedAtMs = (task) => (task?.createdAt ? new Date(task.createdAt).getTime() : 0);

  // Create a copy of the array before sorting to avoid mutating Redux state
  const filteredTasks = [...filteredTasksArray].sort((a, b) => {
    const urgentDiff = compareUrgentFirst(a, b, { isUrgent: isOpenUrgentTask });
    if (urgentDiff !== 0) return urgentDiff;
    const dateA = taskCreatedAtMs(a);
    const dateB = taskCreatedAtMs(b);
    return sortOrder === 'oldest' ? dateA - dateB : dateB - dateA;
  });

  if (loading && tasks.length === 0) {
    return (
      <div
        className={`flex items-center justify-center border-b border-gray-200 dark:border-gray-700 dark:bg-gray-900 ${
          embeddedInChat ? 'min-h-[12rem] flex-1' : 'h-64'
        }`}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div
      className={
        embeddedInChat
          ? 'h-full min-h-0 w-full max-w-none overflow-x-hidden bg-gradient-to-b from-sky-50 via-violet-50/40 to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 p-4 pb-8'
          : 'max-w-6xl mx-auto p-6 bg-gradient-to-b from-sky-50/80 via-white to-violet-50/40 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 min-h-full'
      }
    >
      {!embeddedInChat ? (
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        <Link to="/chat">
          <span className="px-4 py-2 bg-white dark:bg-gray-800 text-sky-700 dark:text-sky-300 border border-sky-100 dark:border-gray-700 rounded-full hover:bg-sky-50 dark:hover:bg-gray-700 transition-colors inline-flex items-center shadow-sm">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 32 32"
              className="w-4 h-4 inline-block mr-2 align-middle"
            >
              <path
                d="M32 15H3.41l8.29-8.29-1.41-1.42-10 10a1 1 0 0 0 0 1.41l10 10 1.41-1.41L3.41 17H32z"
                data-name="4-Arrow Left"
                fill="currentColor"
              />
            </svg>
            Back to Chat
          </span>
        </Link>
      </p>
      ) : null}

      <div className="mb-6 rounded-2xl bg-gradient-to-r from-sky-500 via-indigo-500 to-violet-500 p-[1px] shadow-lg shadow-indigo-200/40 dark:shadow-none">
        <div className="rounded-2xl bg-white/90 dark:bg-gray-900/90 px-5 py-4 flex items-center justify-between gap-4 backdrop-blur-sm">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-sky-700 via-indigo-600 to-violet-600 dark:from-sky-300 dark:via-indigo-300 dark:to-violet-300 bg-clip-text text-transparent mb-1">
            Task Management
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Create, update, and manage your tasks
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-gradient-to-r from-sky-500 to-violet-500 text-white rounded-full hover:from-sky-600 hover:to-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 transition-all shadow-md shadow-violet-200/50 dark:shadow-none whitespace-nowrap"
        >
          <svg
            className="w-5 h-5 inline-block mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Create Task
        </button>
        </div>
      </div>

      {activeTab !== 'analytics' ? (
        <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'All open', value: tabCounts.all, wrap: 'from-sky-400 to-cyan-400', chip: 'bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-200' },
            { label: 'My tasks', value: tabCounts.my, wrap: 'from-emerald-400 to-teal-400', chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200' },
            { label: 'To me', value: tabCounts.delegated, wrap: 'from-amber-400 to-orange-400', chip: 'bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200' },
            { label: 'I delegated', value: tabCounts.delegatedByMe, wrap: 'from-violet-400 to-fuchsia-400', chip: 'bg-violet-50 text-violet-700 dark:bg-violet-950/50 dark:text-violet-200' },
          ].map((item) => (
            <div key={item.label} className={`rounded-2xl bg-gradient-to-br ${item.wrap} p-[1px]`}>
              <div className={`rounded-2xl px-3 py-2.5 ${item.chip}`}>
                <p className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{item.label}</p>
                <p className="text-2xl font-bold leading-tight">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {activeTab !== 'analytics' && (
      <div className="mb-5 rounded-2xl border border-white/80 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-3">
        <div className="relative">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            placeholder="Search tasks by title, description or CUMIN key..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              title="Clear search"
            >
              <svg className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={showWorkflowTasks}
              onChange={(event) => setShowWorkflowTasks(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
            />
            Workflow task
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={showPersonalTasks}
              onChange={(event) => setShowPersonalTasks(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
            />
            Personal
          </label>
          <label className="ml-auto inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <span className="whitespace-nowrap">Sort</span>
            <select
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
              className="px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
          </label>
        </div>
      </div>
      )}
      <div className="mb-5 flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'All Tasks', count: tabCounts.all, tone: 'sky' },
          { id: 'my', label: 'My Tasks', count: tabCounts.my, tone: 'emerald' },
          { id: 'delegated', label: 'Delegated To Me', count: tabCounts.delegated, tone: 'amber' },
          { id: 'delegated-by-me', label: 'I Delegated', count: tabCounts.delegatedByMe, tone: 'violet' },
          { id: 'analytics', label: 'Analytics', count: 0, tone: 'rose' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setActiveTab(tab.id);
              setFilterStatus(null);
              setOpenClosedTab('pending');
            }}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold transition-all ${tabToneClass(tab.tone, activeTab === tab.id)}`}
          >
            {tab.label}
            {tab.id !== 'analytics' && tab.count > 0 ? (
              <span className={`min-w-[1.25rem] px-1.5 py-0.5 text-[11px] font-bold rounded-full ${
                activeTab === tab.id ? 'bg-white/25 text-white' : 'bg-white/80 dark:bg-black/20'
              }`}>
                {tab.count}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {(activeTab === 'all' || activeTab === 'my') && (
        <div className="mb-6 inline-flex rounded-full bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 p-1 shadow-sm">
          {[
            { id: 'pending', label: 'Pending', tone: 'pending' },
            { id: 'completed', label: 'Completed', tone: 'done' },
          ].map((chip) => {
            const source = activeTab === 'my'
              ? tasks.filter((task) => {
                const ids = Array.isArray(task.delegateId) ? task.delegateId : (task.delegateId ? [task.delegateId] : []);
                const delegatedToOthers = ids.filter((id) => id !== user?.id);
                return task.createdByUserId === user?.id && delegatedToOthers.length === 0;
              })
              : tasks.filter((task) => {
                const ids = Array.isArray(task.delegateId) ? task.delegateId : (task.delegateId ? [task.delegateId] : []);
                const delegatedToOthers = ids.filter((id) => id !== user?.id);
                const isDelegatedByMe = task.createdByUserId === user?.id && delegatedToOthers.length > 0;
                return !isDelegatedByMe;
              });
            const count = chip.id === 'completed'
              ? source.filter((task) => task.status === TaskStatus.COMPLETED).length
              : source.filter((task) => task.status !== TaskStatus.COMPLETED && task.status !== TaskStatus.CANCELLED).length;
            return (
              <button
                key={chip.id}
                type="button"
                onClick={() => setOpenClosedTab(chip.id)}
                className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${statusChipClass(openClosedTab === chip.id, chip.tone)} ${openClosedTab === chip.id ? 'border-transparent' : 'border-transparent'}`}
              >
                {chip.label}
                <span className={`ml-2 px-1.5 py-0.5 text-[11px] font-bold rounded-full ${
                  openClosedTab === chip.id ? 'bg-white/25' : 'bg-black/5 dark:bg-white/10'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Analytics Dashboard */}
      {activeTab === 'analytics' && <ProductivityDashboard />}

      {/* Tasks List */}
      {activeTab !== 'analytics' && (
        filteredTasks.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No tasks found
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchQuery
                ? `No tasks found matching "${searchQuery}"`
                : !showWorkflowTasks && !showPersonalTasks
                  ? 'Turn on Workflow task or Personal to see tasks.'
                  : showWorkflowTasks && !showPersonalTasks
                    ? 'No workflow tasks match these filters.'
                    : !showWorkflowTasks && showPersonalTasks
                      ? 'No personal tasks match these filters.'
                : activeTab === 'my' && openClosedTab === 'completed'
              ? 'No completed tasks yet.'
              : activeTab === 'my'
              ? 'You have no pending tasks. Get started by creating a new task.'
              : activeTab === 'delegated'
                ? 'No tasks have been delegated to you yet.'
                : activeTab === 'delegated-by-me'
                  ? 'You haven\'t delegated any tasks to others yet.'
                  : openClosedTab === 'completed'
                    ? 'No completed tasks yet.'
                    : 'You have no pending tasks. Get started by creating a new task'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTasks.map((task) => {
            const isExpanded = expandedTaskIds.has(task.id);
            const isUrgentTask = isOpenUrgentTask(task);
            const urgentEstimateMinutes = Number(task.estimateTimeInMinutes) > 0
              ? Number(task.estimateTimeInMinutes)
              : undefined;
            return (
              <UrgentTaskCardShell key={task.id} task={task} isUrgent={isUrgentTask}>
                {/* Header - Always visible */}
                <div className="flex items-center gap-3 p-1.5 sm:p-1.5">
                  {/* Left side - Task header content */}
                  <div className="flex-1 min-w-0 flex items-center gap-3">
                    {/* Chevron icon for accordion */}
                    <button
                      onClick={() => toggleTaskAccordion(task.id)}
                      className="flex-shrink-0 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <svg
                        className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>

                    <div className="flex-1 min-w-0 cursor-pointer"

                      onClick={() => {
                        setViewingTask(task);
                        setOpenMenuId(null);
                      }}
                    >
                      <div className="group/title flex items-center gap-1 mb-0.5 min-w-0">
                        {renamingTaskId === task.id ? (
                          <input
                            autoFocus
                            value={renameDraft}
                            onChange={(e) => setRenameDraft(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                saveRenameTask(task);
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelRenameTask();
                              }
                            }}
                            onBlur={() => saveRenameTask(task)}
                            className="w-full min-w-0 text-lg font-semibold text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-primary-300 dark:border-primary-600 rounded-md px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            aria-label="Task name"
                          />
                        ) : (
                          <>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate flex-1 min-w-0">
                              {task.title || getInitialWordsFromDescription(task.description)}
                            </h3>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                beginRenameTask(task);
                              }}
                              className="flex-shrink-0 p-1 rounded-md text-gray-400 opacity-0 pointer-events-none group-hover/title:opacity-100 group-hover/title:pointer-events-auto focus:opacity-100 focus:pointer-events-auto hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                              title="Rename task"
                              aria-label="Rename task"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 11l6.232-6.232a2.5 2.5 0 113.536 3.536L12.536 14.536a2 2 0 01-.878.513l-3.09.883a.5.5 0 01-.62-.62l.883-3.09a2 2 0 01.513-.878z" />
                              </svg>
                            </button>
                          </>
                        )}
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {isUrgentTask ? (
                            <>
                              <UrgentTag
                                deadlineAt={task.urgentDeadlineAt}
                                estimateMinutes={urgentEstimateMinutes}
                              />
                              <UrgentCountdownBadge
                                deadlineAt={task.urgentDeadlineAt}
                                estimateMinutes={urgentEstimateMinutes}
                                compact
                              />
                            </>
                          ) : null}
                          {task.cuminIssueKey && (
                            <a
                              href={task.cuminIssueUrl || `${CUMIN_API_URL}/issues/${task.cuminIssueKey}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors whitespace-nowrap"
                              title={`CUMIN Issue: ${task.cuminIssueKey}`}
                            >
                              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              <span className="truncate max-w-[100px]">{task.cuminIssueKey}</span>
                            </a>
                          )}
                        {task.createdAt && (
                            <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            {formatRelativeTime(task.createdAt)}
                          </span>
                        )}
                      </div>
                      </div>
                      {task.status !== TaskStatus.CANCELLED && (
                        <div className="mb-2" onClick={(e) => e.stopPropagation()}>
                          <TaskProgressControl
                            compact
                            value={normalizeTaskProgress(task.completionPercentage, task.status)}
                            disabled={processing[task.id] === 'progress' || processing[task.id] === 'completing' || processing[task.id] === 'updating'}
                            onChange={(pct) => handleProgressChange(task.id, pct)}
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            task.status
                          )}`}
                        >
                          {getStatusLabel(task.status)}
                        </span>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            taskShowsWorkflowTag(task, knownWorkflowIds)
                              ? 'border border-indigo-200/90 bg-indigo-50 text-indigo-800 dark:border-indigo-700/60 dark:bg-indigo-900/25 dark:text-indigo-200'
                              : 'border border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/40 dark:text-gray-300'
                          }`}
                        >
                          {taskShowsWorkflowTag(task, knownWorkflowIds) ? 'Workflow task' : 'Personal'}
                        </span>
                        {taskShowsWorkflowTag(task, knownWorkflowIds) ? (() => {
                          const source = resolveTaskWorkflowChannelLabel(task, {
                            channels,
                            directMessages,
                            users,
                            currentUserId: user?.id,
                            messagesByChannel,
                          });
                          if (!source?.label) return null;
                          return (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                source.kind === 'dm'
                                  ? 'border border-blue-200/90 bg-blue-50 text-blue-800 dark:border-blue-700/60 dark:bg-blue-900/25 dark:text-blue-200'
                                  : 'border border-emerald-200/90 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-900/25 dark:text-emerald-200'
                              }`}
                              title={source.label}
                            >
                              {source.label}
                            </span>
                          );
                        })() : null}
                        {task.estimateTimeInMinutes && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" title="Estimate Time">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {formatEstimateTime(task.estimateTimeInMinutes)}
                          </span>
                        )}
                        <select
                          value={task.status}
                          onChange={(e) => handleStatusChange(task.id, e.target.value)}
                          disabled={processing[task.id] === 'updating'}
                          onClick={(e) => e.stopPropagation()}
                          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                        >
                          <option value={TaskStatus.PENDING}>Pending</option>
                          <option value={TaskStatus.PICK_TODAY}>Pick today</option>
                          <option value={TaskStatus.IN_PROGRESS}>In Progress</option>
                          <option value={TaskStatus.COMPLETED}>Completed</option>
                          <option value={TaskStatus.CANCELLED}>Cancelled</option>
                        </select>
                        {/* Delegate Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDelegateDialog(task);
                          }}
                          className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                          title="Delegate task"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                          </svg>
                        </button>
                        {/* Delegation Info - At the end of the line */}
                        {(() => {
                          const delegationInfo = getDelegationInfo(task);
                          if (delegationInfo) {
                            return (
                              <DelegationInfoDisplay
                                delegationInfo={delegationInfo}
                                variant="compact"
                                className="ml-auto basis-full sm:basis-auto justify-end max-w-full"
                              />
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Right side - Three dot menu */}
                  <div className="relative flex-shrink-0 task-menu-container">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === task.id ? null : task.id)}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="More options"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                        />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {openMenuId === task.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                        <div className="py-1">
                          {/* View Task */}
                          <button
                            onClick={() => {
                              setViewingTask(task);
                              setOpenMenuId(null);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <svg
                              className="w-5 h-5 text-blue-600 dark:text-blue-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                            <span>View Task</span>
                          </button>

                          {/* View Message */}
                          {task.messageId && (
                            <button
                              onClick={() => {
                                handleTaskClick(task);
                                setOpenMenuId(null);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              <svg
                                className="w-5 h-5 text-green-600 dark:text-green-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                />
                              </svg>
                              <span>View Message</span>
                            </button>
                          )}

                          {/* Raise CUMIN Issue */}
                          <button
                            onClick={() => {
                              setTaskForCuminIssue(task);
                              setShowCuminIssueModal(true);
                              setOpenMenuId(null);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <svg
                              className="w-5 h-5 text-purple-600 dark:text-purple-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                              />
                            </svg>
                            <span>Raise CUMIN Issue</span>
                          </button>

                          {/* Edit Task */}
                          <button
                            onClick={() => {
                              handleEdit(task);
                              setOpenMenuId(null);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <svg
                              className="w-5 h-5 text-blue-600 dark:text-blue-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                            <span>Edit Task</span>
                          </button>

                          {/* Mark as Completed */}
                          {task.status !== TaskStatus.COMPLETED && (
                            <button
                              onClick={() => {
                                handleMarkAsCompleted(task.id);
                                setOpenMenuId(null);
                              }}
                              disabled={processing[task.id] === 'completing'}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {processing[task.id] === 'completing' ? (
                                <>
                                  <svg className="w-5 h-5 text-green-600 dark:text-green-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span>Completing...</span>
                                </>
                              ) : (
                                <>
                                  <svg
                                    className="w-5 h-5 text-green-600 dark:text-green-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                  <span>Mark as Completed</span>
                                </>
                              )}
                            </button>
                          )}

                          {/* Divider - Only show if delete button is visible */}
                          {task.createdByUserId === user?.id && (
                            <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                          )}

                          {/* Delete Task - Only show for task creator */}
                          {task.createdByUserId === user?.id && (
                            <button
                              onClick={() => {
                                handleDelete(task.id);
                                setOpenMenuId(null);
                              }}
                              disabled={processing[task.id] === 'deleting'}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {processing[task.id] === 'deleting' ? (
                                <>
                                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span>Deleting...</span>
                                </>
                              ) : (
                                <>
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                  <span>Delete Task</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Collapsible content */}
                {isExpanded && (
                  <div className="px-2 sm:px-3 pb-2 sm:pb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                    {/* Primary Task Description */}
                    <div className="mb-4">
                      <div className="flex items-start gap-2">
                        <div className="text-sm text-gray-600 dark:text-gray-400 flex-1 bg-gray-50 dark:bg-gray-900 rounded-lg p-2">
                          {/* Check if description is HTML (rich text) */}
                          {task.description && task.description.trim().startsWith('<') && task.description.includes('</') ? (
                            <div
                              className="ql-editor-content"
                              dangerouslySetInnerHTML={{ __html: task.description }}
                              style={{
                                fontSize: '0.875rem',
                                lineHeight: '1.5',
                              }}
                            />
                          ) : (
                            <p className="whitespace-pre-wrap">{task.description || 'No description'}</p>
                          )}
                        </div>
                        {/* Arrow button to navigate to message */}
                        {task.messageId && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskClick(task);
                            }}
                            className="flex-shrink-0 p-1.5 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            title="Go to message"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                      {/* Display image attachments from message */}
                      {task.messageId && taskMessagesByTaskId[task.id]?.attachments && (
                        renderTaskAttachments(taskMessagesByTaskId[task.id].attachments)
                      )}
                    </div>

                    {/* Related Messages - Show below primary description with separator */}
                    {(() => {
                      const taskMappedTasks = mappedTasksByTaskId[task.id] || [];
                      const isLoading = loadingMappedTasksByTaskId[task.id];
                      
                      if (taskMappedTasks.length > 0 || isLoading) {
                        return (
                          <>
                            {/* Separator */}
                            {taskMappedTasks.length > 0 && (
                              <div className="my-4 border-t border-gray-300 dark:border-gray-600"></div>
                            )}
                            
                            {isLoading ? (
                              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Loading related messages...
                              </div>
                            ) : taskMappedTasks.length > 0 ? (
                              <div className="space-y-4">
                                {taskMappedTasks.map((mappedTask, index) => (
                                  <div key={mappedTask.id}>
                                    {/* Separator between messages (except first one) */}
                                    {index > 0 && (
                                      <div className="my-3 border-t border-gray-200 dark:border-gray-700"></div>
                                    )}
                                    
                                    {/* Related Message Content */}
                                    <div className="flex items-start gap-2">
                                      <div className="flex-1 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 rounded-lg p-2">
                                        {/* Check if description is HTML (rich text) */}
                                        {mappedTask.description && mappedTask.description.trim().startsWith('<') && mappedTask.description.includes('</') ? (
                                          <div
                                            className="ql-editor-content"
                                            dangerouslySetInnerHTML={{ __html: mappedTask.description }}
                                            style={{
                                              fontSize: '0.875rem',
                                              lineHeight: '1.5',
                                            }}
                                          />
                                        ) : (
                                          <p className="whitespace-pre-wrap">{mappedTask.description || 'No description provided.'}</p>
                                        )}
                                      </div>
                                      {/* Arrow button to navigate to message if messageId exists */}
                                      {mappedTask.messageId && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTaskClick(mappedTask);
                                          }}
                                          className="flex-shrink-0 p-1.5 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                          title="Go to message"
                                        >
                                          <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M9 5l7 7-7 7"
                                            />
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </>
                        );
                      }
                      return null;
                    })()}

                    <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-4 gap-y-1 mt-4">
                      {(() => {
                        const delegationInfo = getDelegationInfo(task);
                        if (delegationInfo) {
                          return (
                            <DelegationInfoDisplay
                              delegationInfo={delegationInfo}
                              variant="full"
                              className="min-w-0 max-w-full"
                            />
                          );
                        }
                        return null;
                      })()}
                      {task.completed && (
                        <div>
                          <span className="font-medium">Completed:</span> {formatDate(task.completed)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </UrgentTaskCardShell>
            );
          })}
        </div>
      ))}

      {viewingTask && (
        <TaskViewDialog
          task={viewingTask}
          mappedMessagesByTaskId={taskMessagesByTaskId}
          onClose={() => setViewingTask(null)}
          onEdit={handleEdit}
          onProgressChange={(pct) => handleProgressChange(viewingTask.id, pct)}
          onNavigateToMessage={(task) => {
                    setViewingTask(null);
            handleTaskClick(task);
          }}
          onOpenWorkflow={(item) => {
                  setViewingTask(null);
            if (item?.messageId) {
              window.dispatchEvent(new CustomEvent('workloop:open-workflow', { detail: item }));
            }
          }}
        />
      )}

      <TaskCreationDialog
        isOpen={showCreateModal}
        onClose={closeTaskFormDialog}
        initialDescription=""
        editingTask={editingTask}
        onSaved={handleTaskFormSaved}
      />

      {/* Delegate Task Dialog */}
      {showDelegateDialog && delegatingTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Delegate Task
                </h2>
                <button
                  onClick={() => {
                    setShowDelegateDialog(false);
                    setDelegatingTask(null);
                    setSelectedDelegateIds([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {delegatingTask.title || getInitialWordsFromDescription(delegatingTask.description, 10)}
                </p>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Delegate To
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white max-h-48 overflow-y-auto space-y-2">
                  {usersLoading ? (
                    <div className="text-sm text-gray-500 italic">Loading users...</div>
                  ) : users.length === 0 ? (
                    <div className="text-sm text-gray-500 italic">No users available</div>
                  ) : (
                    users
                      .filter(u => u.id !== user?.id) // Exclude current user
                      .map((u) => (
                        <label key={u.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedDelegateIds.includes(u.id)}
                            onChange={() => toggleSelectedDelegate(u.id)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm">
                            {u.firstName && u.lastName
                              ? `${u.firstName} ${u.lastName} (${u.username})`
                              : u.username || u.email}
                          </span>
                        </label>
                      ))
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Select one or more users to delegate this task to.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDelegateDialog(false);
                    setDelegatingTask(null);
                    setSelectedDelegateIds([]);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelegateTask}
                  disabled={processing[delegatingTask.id] === 'delegating'}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {processing[delegatingTask.id] === 'delegating'
                    ? 'Delegating...'
                    : 'Delegate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CUMIN Issue Creation Modal */}
      <CreateCuminIssueModal
        isOpen={showCuminIssueModal}
        onClose={() => {
          setShowCuminIssueModal(false);
          setTaskForCuminIssue(null);
        }}
        task={taskForCuminIssue}
        onSuccess={(issueData, taskId) => {
          // Refresh tasks to show the CUMIN issue tag
          if (activeTab === 'all') {
            dispatch(fetchTasks({ status: filterStatus }));
          } else if (activeTab === 'my') {
            dispatch(fetchMyTasks());
          } else if (activeTab === 'delegated') {
            dispatch(fetchDelegatedTasks());
          } else if (activeTab === 'delegated-by-me') {
            dispatch(fetchTasksIDelegated());
          }

          dispatch(addNotification({
            message: `CUMIN issue created successfully: ${issueData.issueKey || issueData.issueId}`,
            type: 'success'
          }));
        }}
      />

      {/* Image Viewer Modal */}
      {viewingImage && (
        <ImageViewerModal
          imageUrl={viewingImage.url}
          fileName={viewingImage.fileName}
          onClose={() => setViewingImage(null)}
        />
      )}
    </div>
  );
};

export default TaskManagementPanel;


